// See: https://docs.google.com/document/d/1A1f_vEOSN8iGEbDO8SUibmA2oXeAZ_gf-WOgnCwxy-A/edit?pli=1&tab=t.0
// for details behind these scores and their components.
export const VALID_NUMERIC_USER_SCORE_KINDS = [
  // The overall energy score (0-100) calculated based on its components.
  'ENERGY',

  // The energy score (0-100) told by user in their latest experience check-in.
  'ENERGY_CHECK_IN',

  // The energy score (0-100) told by user in their latest experience check-out.
  'ENERGY_CHECK_OUT',

  // The energy score (0-100) told by user in their latest weekly reflection
  // (not yet implemented).
  'ENERGY_WEEKLY_REFLECTION',

  // The check-out question about the immediate impact of a wellbeing session on
  // the user state is asked in check-outs for sessions with "IMMEDIATE_STATE_IMPACT"
  // and "IMMEDIATE_STATE_IMPACT_WITH_DELAYED_MOMENT_IMPACT" kinds, the response,
  // normalized to (0-100) scale, is stored to the user score collection. It is
  // not subsequently used for anything yet.
  'IMMEDIATE_STATE_IMPACT',

  // The overall intention score (0-100) calculated based on its components.
  'INTENTION',

  // Intention presence at check-in (0-100). It is calculated internally,
  // based on INTENTION_CHECK_IN_STRING records, and it is essentially
  // the percentage of the last 7 days when the user has stated their intention
  // at least in a single session check-in.
  'INTENTION_CHECK_IN_NUMBER',

  // Weekly intention score (0-100), asked from user in their weekly reflection
  // (not yet implemented).
  'INTENTION_WEEKLY_REFLECTION',

  // The number of experience sessions completed within the last 30 days.
  'PRACTICE_COMPLETION_COUNT_30D',

  // Percent (0-100) of experience sessions completed within the last 7 days.
  'PRACTICE_COMPLETION_PERCENTAGE_7D',

  // Percent (0-100) of experience sessions completed within the last 30 days.
  'PRACTICE_COMPLETION_PERCENTAGE_30D',

  // The practice consistency score (0-100) is essentially the percentage of
  // last 7 days on which the user has completed at least one experience session.
  'PRACTICE_CONSISTENCY',

  // The total number of experience sessions started by user in their lifetime.
  'PRACTICE_STARTED_COUNT_LIFETIME',

  // The overall presence score (0-100) calculated based on its components.
  'PRESENCE',

  // Daily presence score (0-100), asked in the experience check-in once per day.
  'PRESENCE_CHECK_IN_DAILY',

  // Presence improvements score (0-100), asked in the experience check out.
  'PRESENCE_CHECK_OUT',

  // Weekly presence reflection (0-100), asked in the weekly reflection
  // (not implemented yet).
  'PRESENCE_WEEKLY_REFLECTION',

  // The transformation score (0-100) calculated based on ENERGY, INTENTION,
  // and PRESENSE scores.
  'TRANSFORMATION',

  /**
   * @deprecated - use "PRACTICE_COMPLETION_PERCENTAGE_7D" instead.
   * Remove once the DB patch #07 is applied by everybody (some days after
   * July 1, 2026).
   */
  'PRACTICE_COMPLETION',
] as const;

/**
 * These are for the stuff like "Modality profile", "Most experienced artists",
 * etc. Essentially, every score record of these kinds contains an array of tuplets,
 * each tuplet has a string as its first element, and a number as its second element;
 * their exact meaning is commented below.
 */
export const VALID_RANKING_USER_SCORE_KINDS = [
  // In these records the strings are artist (practitioner) IDs, and the numbers
  // are the total number of experience sessions, done by the user, that contained
  // some content from that artist.
  'ARTIST_EXPERIENCE_COUNT',

  // In these records the strings are artist (practitioner) IDs, and the numbers
  // are the total number of experience sessions, done by the user, after which
  // (in the check-out) the user called out that artist as impactful.
  'ARTIST_IMPACT_COUNT',

  // The records are created in the end of check-out interviews, and store IDs
  // of the artists (if any) the user mentioned as impactful in that session.
  // The all numbers in these records are assign equal to 1, and carry no
  // particular meaning.
  'IMPACTFUL_ARTISTS',

  // The records are created in the end of check-out interviews, and store names
  // of modalities (if any) the user mentioned as impactful in that session.
  // The all numbers in these records are assign equal to 1, and carry no
  // particular meaning.
  'IMPACTFUL_MODALITIES',

  // In these records the strings are wellbeing modality names, and the numbers
  // are the total number of experience sessions, done by the user, that included
  // that modality.
  'MODALITY_EXPERIENCE_COUNT',

  // In these records the strings are wellbeing modality names, and the numbers
  // are the total number of experience sessions, done by the user, after which
  // (in the check-out) the user called out that modality as impactful.
  'MODALITY_IMPACT_COUNT',
] as const;

export const VALID_TEXTUAL_USER_SCORE_KINDS = [
  // Asked in every experience check-in, "What is your primary intention today?"
  // if user replies it counts towards the "INTENTION_CHECK_IN_NUMBER" numeric
  // score.
  'INTENTION_CHECK_IN_STRING',

  // Asked in the "Monthly Reflection" interview (not yet implemented);
  // "Which areas felt most aligned with your values?"
  'INTENTION_MONTHLY_REFLECTION',
] as const;

export const VALID_USER_SCORE_KINDS = [
  ...VALID_NUMERIC_USER_SCORE_KINDS,
  ...VALID_RANKING_USER_SCORE_KINDS,
  ...VALID_TEXTUAL_USER_SCORE_KINDS,
] as const;

export type NumericUserScoreKindT
  = typeof VALID_NUMERIC_USER_SCORE_KINDS[number];

export type RankingUserScoreKindT
  = typeof VALID_RANKING_USER_SCORE_KINDS[number];

export type TextualUserScoreKindT
  = typeof VALID_TEXTUAL_USER_SCORE_KINDS[number];

export type UserScoreKindT = typeof VALID_USER_SCORE_KINDS[number];

export type SelectedUserScoresT<K extends UserScoreKindT>
  = Record<K, UserScoreT | undefined>;

export function isNumericUserScoreKind(
  kind: UserScoreKindT,
): kind is NumericUserScoreKindT {
  return VALID_NUMERIC_USER_SCORE_KINDS.includes(
    kind as NumericUserScoreKindT,
  );
}

export function isRankingUserScoreKind(
  kind: UserScoreKindT,
): kind is RankingUserScoreKindT {
  return VALID_RANKING_USER_SCORE_KINDS.includes(
    kind as RankingUserScoreKindT,
  );
}

export function isTextualUserScoreKind(
  kind: UserScoreKindT,
): kind is TextualUserScoreKindT {
  return VALID_TEXTUAL_USER_SCORE_KINDS.includes(
    kind as TextualUserScoreKindT,
  );
}

export type BaseUserScoreT<
  K extends
  | NumericUserScoreKindT
  | RankingUserScoreKindT
  | TextualUserScoreKindT,
  T,
> = {
  id: string;
  kind: K;
  timestamp: number;
  user: string;
  value: T;
};

export type NumericUserScoreT = BaseUserScoreT<NumericUserScoreKindT, number>;

export type RankingUserScoreT = BaseUserScoreT<
  RankingUserScoreKindT,
  Array<[string, number]>
>;

export type TextualUserScoreT = BaseUserScoreT<TextualUserScoreKindT, string>;

export type UserScoreT
  = | NumericUserScoreT | RankingUserScoreT | TextualUserScoreT;
